/**
 * Authentication provider.
 *
 * Behaviour is controlled by the VITE_AUTH_ENABLED environment variable:
 *
 *   VITE_AUTH_ENABLED=false (default)
 *     – Demo / development mode.  The app is always considered authenticated
 *       as a superadmin user.  No login screen is shown.  Useful for local
 *       development and demos without a running identity provider.
 *
 *   VITE_AUTH_ENABLED=true
 *     – Real authentication mode.  The user must log in via the configured
 *       identity provider (Keycloak by default).  The session and user profile
 *       are persisted in localStorage.  Logging out clears both.
 *
 * To switch to Keycloak locally:
 *   1. Start the keycloak service: docker compose up keycloak
 *   2. Set VITE_AUTH_ENABLED=true in your .env.local
 *   3. Log in with demo/demo, admin/admin, or superadmin/superadmin
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { apiClient, attemptRefresh, registerAuthCallbacks } from '../api/client';
import type { AuthSession, UserProfile } from '../api/contracts';
import { ChallengeRequiredError } from './errors';
import { AppRole } from './roles';

// ---------------------------------------------------------------------------
// Feature flag — set VITE_AUTH_ENABLED=true to require login
// ---------------------------------------------------------------------------
export const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED === 'true';

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------
const SESSION_KEY = 'polaris.auth.session';
const PROFILE_KEY = 'polaris.auth.profile';

// Trigger proactive refresh at 75% of the access token's lifetime. Leaves a
// comfortable margin for clock skew and slow networks while avoiding churn
// at the very end of the validity window.
const REFRESH_AT_FRACTION = 0.75;
// Safety margin before hard expiry — never wait longer than (expiresAt - 30s)
// even if 75% would fall later (short-lived tokens).
const REFRESH_SAFETY_MARGIN_MS = 30_000;
// Minimum delay before the first refresh fires. Guards against negative or
// tiny timers if the saved session is already past its refresh threshold.
const MIN_REFRESH_DELAY_MS = 10_000;
const MS_PER_SECOND = 1000;

function readStorage<T>(key: string): T | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function clearStorage(...keys: string[]): void {
  if (typeof window === 'undefined') return;
  for (const key of keys) window.localStorage.removeItem(key);
}

// ---------------------------------------------------------------------------
// Demo session / profile (used when AUTH_ENABLED=false)
// ---------------------------------------------------------------------------
const DEMO_SESSION: AuthSession = {
  access_token: 'local-demo-token',
  provider: 'demo',
  token_type: 'Bearer',
  tenant_id: 'tenant-1',
  organization: null,
};

const DEMO_PROFILE: UserProfile = {
  user_id: 'demo-user',
  tenant_id: 'tenant-1',
  email: 'demo@polaris.local',
  display_name: 'Demo User',
  roles: [AppRole.SUPERADMIN],
  organization: null,
  // PCS-429: keep every gated feature visible in demo mode so local
  // dev (VITE_AUTH_ENABLED=false) doesn't go dark on flag rollouts.
  // Real auth pulls this from the BFF /me response.
  features: { sftp_upload: true },
};

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------
export type AuthContextValue = {
  /** True when a valid session is present (always true in demo mode). */
  isAuthenticated: boolean;
  /** Raw session token data. */
  session: AuthSession | undefined;
  /** Resolved user profile including role list. */
  profile: UserProfile | undefined;
  /** Identity provider name shown in the UI. */
  providerName: string;
  /** Sign in and populate session + profile. Throws ChallengeRequiredError when IdP requires a password reset. */
  login: (username: string, password: string, tenantId: string) => Promise<void>;
  /** Complete a NEW_PASSWORD_REQUIRED challenge after login returned challenge_name. */
  respondToChallenge: (username: string, newPassword: string, challengeSession: string, tenantId: string) => Promise<void>;
  /** Clear session and profile.  No-op in demo mode. */
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: true,
  session: DEMO_SESSION,
  profile: DEMO_PROFILE,
  providerName: 'demo',
  login: async () => undefined,
  respondToChallenge: async () => undefined,
  logout: () => undefined,
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | undefined>(() => {
    if (!AUTH_ENABLED) return DEMO_SESSION;
    const stored = readStorage<AuthSession>(SESSION_KEY);
    // Stamp token_obtained_at if missing (pre-refresh-era sessions)
    if (stored && !stored.token_obtained_at) {
      stored.token_obtained_at = Date.now();
      writeStorage(SESSION_KEY, stored);
    }
    return stored;
  });

  const [profile, setProfile] = useState<UserProfile | undefined>(() =>
    AUTH_ENABLED ? readStorage<UserProfile>(PROFILE_KEY) : DEMO_PROFILE,
  );

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  const isAuthenticated = AUTH_ENABLED ? !!session?.access_token : true;
  const providerName = session?.provider ?? 'demo';

  // ── Register auth callbacks so the API client can trigger reactive refresh ──
  useEffect(() => {
    if (!AUTH_ENABLED) return;
    registerAuthCallbacks({
      getSession: () => sessionRef.current,
      onRefresh: (refreshed) => {
        setSession(refreshed);
        writeStorage(SESSION_KEY, refreshed);
      },
      onRefreshFail: () => {
        setSession(undefined);
        setProfile(undefined);
        clearStorage(SESSION_KEY, PROFILE_KEY);
      },
    });
  }, []);

  // ── Refresh the profile from /me on every authenticated mount and
  // ── whenever the session changes (login, token refresh, tenant
  // ── switch). The cached localStorage profile drives first paint;
  // ── this effect overwrites it once /me responds, so feature-flag
  // ── rollout changes (allowlist edits, kill-switch flips) propagate
  // ── without forcing the user to log out and back in. Failures are
  // ── non-fatal — the cached profile sticks around and is logged so
  // ── an on-call engineer debugging "why doesn't $user see the
  // ── tile?" has a trail to follow.
  //
  // ── Dep list intentionally excludes ``profile``: the success
  // ── callback calls ``setProfile`` and that must not re-trigger
  // ── this effect. Token / tenant changes are the only triggers
  // ── that should cause a refetch.
  useEffect(() => {
    if (!AUTH_ENABLED) return;
    if (!session?.access_token) return;
    let cancelled = false;
    void apiClient
      .getMe({
        accessToken: session.access_token,
        tenantId: session.tenant_id ?? undefined,
      })
      .then((refreshed) => {
        if (cancelled) return;
        setProfile(refreshed);
        writeStorage(PROFILE_KEY, refreshed);
      })
      .catch((err: unknown) => {
        console.warn(
          '[AuthProvider] /me profile refresh failed — keeping cached profile',
          err,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [session?.access_token, session?.tenant_id]);

  // ── Proactive token refresh timer ──────────────────────────────────────────
  useEffect(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (!AUTH_ENABLED || !session?.expires_in || !session?.refresh_token) return;

    const obtainedAt = session.token_obtained_at ?? Date.now();
    const lifetimeMs = session.expires_in * MS_PER_SECOND;
    const expiresAtMs = obtainedAt + lifetimeMs;
    const refreshAtMs = Math.min(
      obtainedAt + lifetimeMs * REFRESH_AT_FRACTION,
      expiresAtMs - REFRESH_SAFETY_MARGIN_MS,
    );
    const delayMs = Math.max(refreshAtMs - Date.now(), MIN_REFRESH_DELAY_MS);

    refreshTimerRef.current = setTimeout(async () => {
      await attemptRefresh();
    }, delayMs);

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [session]);

  const finalizeSession = useCallback(async (rawSession: AuthSession, tenantIdFallback: string) => {
    const nextSession: AuthSession = { ...rawSession, token_obtained_at: Date.now() };
    setSession(nextSession);
    writeStorage(SESSION_KEY, nextSession);

    const auth = {
      accessToken: nextSession.access_token,
      tenantId: nextSession.tenant_id ?? tenantIdFallback,
    };
    const nextProfile = await apiClient.getMe(auth);
    setProfile(nextProfile);
    writeStorage(PROFILE_KEY, nextProfile);
  }, []);

  const login = useCallback(async (username: string, password: string, tenantId: string) => {
    const rawSession = await apiClient.login(username, password, tenantId);
    if (rawSession.challenge_name) {
      if (!rawSession.challenge_session) {
        throw new Error('Cognito challenge response is missing a session token. Please try again.');
      }
      throw new ChallengeRequiredError(
        rawSession.challenge_name,
        rawSession.challenge_session,
        username,
        tenantId,
      );
    }
    await finalizeSession(rawSession, tenantId);
  }, [finalizeSession]);

  const respondToChallenge = useCallback(
    async (username: string, newPassword: string, challengeSession: string, tenantId: string) => {
      const rawSession = await apiClient.respondToChallenge(username, newPassword, challengeSession);
      await finalizeSession(rawSession, tenantId);
    },
    [finalizeSession],
  );

  const logout = useCallback(() => {
    if (!AUTH_ENABLED) return;
    // Best-effort server-side token revocation before clearing local state.
    if (session?.access_token) {
      apiClient.logout({ accessToken: session.access_token, tenantId: session.tenant_id ?? undefined }).catch(() => {});
    }
    setSession(undefined);
    setProfile(undefined);
    clearStorage(SESSION_KEY, PROFILE_KEY);
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({ isAuthenticated, session, profile, providerName, login, respondToChallenge, logout }),
    [isAuthenticated, session, profile, providerName, login, respondToChallenge, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
